import { valibotResolver } from "@hookform/resolvers/valibot";
import { Plus } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import DeleteNoteDialog from "common/components/leads/LeadNotes/DeleteNoteDialog";
import { Button } from "common/components/ui/Button";
import FormField from "common/components/ui/FormField";
import useLeadNotes from "common/datahooks/useLeadNotes";
import { NoteSchema, type NoteSchemaType } from "common/schemas";
import type { Note as NoteType } from "common/types";

import TextArea from "../../ui/TextArea";
import Note from "./Note";

import type React from "react";

interface LeadNotesProps {
  notes: NoteType[];
  profileId: string;
}

function LeadNotes({ notes, profileId }: LeadNotesProps) {
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [deleteNoteId, setDeleteNoteId] = useState(null);
  const { addLeadNote, isAddingLeadNote } = useLeadNotes();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NoteSchemaType>({
    resolver: valibotResolver(NoteSchema),
  });

  function onSubmit({ text }: NoteSchemaType) {
    addLeadNote({ profileId, text }).then(() => {
      setIsAddingNote(false);
      reset({ text: "" });
    });
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      handleSubmit(onSubmit)();
    }
  }

  return (
    <div className="-mx-1 -mt-1 flex flex-col gap-y-2 overflow-y-hidden">
      {notes && !!notes.length && (
        <div className="flex flex-col gap-y-2 overflow-y-auto px-1 pt-1 scrollbar-thin">
          {notes.map((note) => (
            <Note
              key={note.id}
              profileId={profileId}
              note={note}
              onDelete={() => setDeleteNoteId(note.id)}
            />
          ))}
        </div>
      )}

      {isAddingNote ? (
        <div className="flex flex-col gap-y-2 px-1">
          <FormField label="" htmlFor="" error={errors.text?.message}>
            <TextArea
              {...register("text")}
              hasError={!!errors.text?.message}
              autoFocus
              minRows={4}
              maxRows={6}
              placeholder="Write something..."
              onKeyDown={onKeyDown}
              disabled={isAddingLeadNote}
            />
          </FormField>

          <div className="flex justify-end gap-x-2">
            <Button
              size="sm"
              variant="outline-black"
              onClick={() => {
                setIsAddingNote(false);
                reset({ text: "" });
              }}
              disabled={isAddingLeadNote}
            >
              Discard
            </Button>

            <Button
              size="sm"
              variant="secondary-purple"
              onClick={handleSubmit(onSubmit)}
              isLoading={isAddingLeadNote}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsAddingNote(true)}
          className="flex items-center gap-x-2 rounded-3xl border border-dashed border-black-300 bg-white px-3.5 py-3 text-button-sm transition-[background] hover:bg-black-100"
        >
          <Button intent="labelIcon" size="xs">
            <Plus />
          </Button>
          Add note
        </button>
      )}

      <DeleteNoteDialog
        profileId={profileId}
        noteId={deleteNoteId}
        onClose={() => setDeleteNoteId(null)}
      />
    </div>
  );
}
LeadNotes.displayName = "LeadNotes";

export default LeadNotes;
